import {ChangeDetectionStrategy, Component, computed, effect, inject, signal} from '@angular/core';
import {rxResource} from '@angular/core/rxjs-interop';
import {FormsModule} from '@angular/forms';
import {TranslocoPipe} from '@jsverse/transloco';
import {Message} from 'primeng/message';
import {SelectButton} from 'primeng/selectbutton';
import {EvaluationCriteria, EvaluationSubCriteriaDirectus} from '@frontend/directus';
import {
  EvaluationCriteriaOverviewResponse,
  EvaluationSubCriteriaOverviewView,
  ProjectChapterType,
  ProjectRoleType,
  ProjectStepType,
} from '@frontend/shared-api';
import {ProjectStepBaseReactive} from '../../../project-base-step/project-base-step-reactive.component';
import {LoadingComponent} from '../../../../../../shared/components/loading/loading.component';
import {StepDotMenuComponent} from '../../../../../../shared/components/step-dot-menu/step-dot-menu.component';
import {CriteriaWeightsTableComponent} from './criteria-weights-table/criteria-weights-table.component';
import {CriteriaWeightsTreeChartComponent} from './criteria-weights-tree-chart/criteria-weights-tree-chart.component';
import {CriteriaWeightsTreemapComponent} from './criteria-weights-treemap/criteria-weights-treemap.component';
import {criteriaColorToken} from './criteria-weights-overview.utils';
import {Step43CriteriaWeightsOverviewService} from './step-4-3-criteria-weights-overview.service';
import {
  CriteriaWeightsCriterion,
  CriteriaWeightsSubCriterion,
  CriteriaWeightsView,
  CriteriaWeightsViewOption,
} from './step-4-3-criteria-weights-overview.types';

@Component({
  selector: 'app-step-4-3-criteria-weights-overview',
  imports: [
    FormsModule,
    TranslocoPipe,
    Message,
    SelectButton,
    LoadingComponent,
    StepDotMenuComponent,
    CriteriaWeightsTableComponent,
    CriteriaWeightsTreeChartComponent,
    CriteriaWeightsTreemapComponent,
  ],
  templateUrl: './step-4-3-criteria-weights-overview.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Step43CriteriaWeightsOverviewComponent extends ProjectStepBaseReactive {
  protected readonly ProjectRoleType = ProjectRoleType;
  protected readonly ProjectStepType = ProjectStepType;
  protected readonly viewOptions: CriteriaWeightsViewOption[] = [
    {value: 'table', translationKey: 'project.steps.viewTable', icon: 'pi pi-table'},
    {
      value: 'tree',
      translationKey: 'project.steps.criteriaWeightsOverview.viewTree',
      icon: 'pi pi-sitemap',
    },
    {
      value: 'treemap',
      translationKey: 'project.steps.criteriaWeightsOverview.viewTreemap',
      icon: 'pi pi-th-large',
    },
  ];

  private readonly service = inject(Step43CriteriaWeightsOverviewService);

  protected readonly view = signal<CriteriaWeightsView>('table');
  private readonly initialLoadDone = signal(false);

  private readonly overviewResource = rxResource({
    params: () => this.projectId(),
    stream: ({params: projectId}) => this.service.loadOverview(projectId),
  });

  private readonly directusCriteriaResource = rxResource({
    stream: () => this.service.loadDirectusCriteria(),
  });

  private readonly directusSubCriteriaResource = rxResource({
    stream: () => this.service.loadDirectusSubCriteria(),
  });

  private readonly resourcesLoading = computed<boolean>(
    () =>
      this.isLoadingState() ||
      this.overviewResource.status() === 'loading' ||
      this.directusCriteriaResource.status() === 'loading' ||
      this.directusSubCriteriaResource.status() === 'loading',
  );

  protected readonly loading = computed<boolean>(
    () => !this.initialLoadDone() && this.resourcesLoading(),
  );

  private readonly directusCriteriaById = computed<Map<string, EvaluationCriteria>>(
    () =>
      new Map(
        (this.directusCriteriaResource.value() ?? []).map((criteria) => [criteria.id, criteria]),
      ),
  );

  private readonly directusSubCriteriaById = computed<Map<string, EvaluationSubCriteriaDirectus>>(
    () =>
      new Map(
        (this.directusSubCriteriaResource.value() ?? []).map((subCriterion) => [
          subCriterion.id,
          subCriterion,
        ]),
      ),
  );

  protected readonly criteria = computed<CriteriaWeightsCriterion[]>(() => {
    const directusCriteriaById = this.directusCriteriaById();
    const directusSubCriteriaById = this.directusSubCriteriaById();

    return (this.overviewResource.value() ?? []).map((criterion) =>
      this.toCriterion(criterion, directusCriteriaById, directusSubCriteriaById),
    );
  });

  protected readonly hasCriteria = computed<boolean>(() => this.criteria().length > 0);

  protected readonly showsIncompleteWeightsHint = computed<boolean>(
    () =>
      !this.stepState.areStepsCompleted([
        {
          chapterId: ProjectChapterType.EVALUATION_CRITERIA,
          stepId: ProjectStepType.EVALUATION_CRITERIA,
        },
        {
          chapterId: ProjectChapterType.EVALUATION_CRITERIA,
          stepId: ProjectStepType.EVALUATION_SUB_CRITERIA,
        },
      ]),
  );

  constructor() {
    super();

    effect(() => {
      if (!this.resourcesLoading()) {
        this.initialLoadDone.set(true);
      }
    });
  }

  private toCriterion(
    criterion: EvaluationCriteriaOverviewResponse,
    directusCriteriaById: Map<string, EvaluationCriteria>,
    directusSubCriteriaById: Map<string, EvaluationSubCriteriaDirectus>,
  ): CriteriaWeightsCriterion {
    return {
      id: criterion.id,
      ecNumber: criterion.ecNumber,
      identifier: `EC${criterion.ecNumber}`,
      translations: directusCriteriaById.get(criterion.directusId)?.translations ?? [],
      color: `var(${criteriaColorToken(criterion.ecNumber)})`,
      weight: criterion.weight ?? null,
      subCriteria: criterion.subCriteria.map((subCriterion) =>
        this.toSubCriterion(subCriterion, directusSubCriteriaById),
      ),
    };
  }

  private toSubCriterion(
    subCriterion: EvaluationSubCriteriaOverviewView,
    directusSubCriteriaById: Map<string, EvaluationSubCriteriaDirectus>,
  ): CriteriaWeightsSubCriterion {
    return {
      id: subCriterion.id,
      identifier: `SC ${subCriterion.scNumber}`,
      translations: directusSubCriteriaById.get(subCriterion.directusId)?.translations ?? [],
      weight: subCriterion.weight ?? null,
      overallWeight: subCriterion.overallWeight ?? null,
    };
  }
}
